import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Carte des players — positions déduites de l'IP publique (`_mac_wifi`, remontée par PUT /setIp).
//
// Leaflet est utilisé en DIRECT (API impérative), volontairement SANS react-leaflet : sa v4 est
// ESM-only et casse la build CRA5 de ce projet. Une ref + un useEffect suffisent.
//
// ⚠️ La géoloc IP donne le POINT DE PRÉSENCE DE L'OPÉRATEUR, pas l'adresse du client. Vérifié sur
// le parc : "TDL - MASSENA" (Paris) ressort à Lyon via son FAI. C'est indicatif — l'UI le dit.

const apiBase = () => `${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}`;

// Marqueur SVG inline : les icônes par défaut de Leaflet pointent des PNG relatifs qui cassent
// après build (chemins réécrits par webpack) -> divIcon, aucun asset externe.
const dot = (color) => L.divIcon({
    className: "",
    html: `<span style="display:block;width:14px;height:14px;border-radius:50%;background:${color};
           border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.35)"></span>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
});

const ONLINE_MS = 5 * 60 * 1000;   // même seuil que le dashboard Now Playing
const COLORS = { online: "#10b981", offline: "#ef4444", unauthorized: "#f59e0b" };

function statusOf(p) {
    if (String(p.authorized).toLowerCase() !== "yes") return "unauthorized";
    const t = p.lastSeen ? new Date(String(p.lastSeen).replace(/\//g, "-")).getTime() : NaN;
    if (!isNaN(t) && Date.now() - t <= ONLINE_MS) return "online";
    return "offline";
}

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

export default function MapPlayers() {
    const [data, setData] = useState({ points: [], totals: {}, accuracy: "" });
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const mapDiv = useRef(null);
    const mapRef = useRef(null);
    const layerRef = useRef(null);

    useEffect(() => {
        let alive = true;
        axios.get(`${apiBase()}/devices_geo`)
            .then((res) => { if (alive) { setData(res.data || { points: [] }); setError(null); } })
            .catch(() => { if (alive) setError("Impossible de récupérer les positions."); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    // Création de la carte : UNE SEULE FOIS (sinon Leaflet lève "Map container is already initialized").
    useEffect(() => {
        if (mapRef.current || !mapDiv.current) return;
        const map = L.map(mapDiv.current, { scrollWheelZoom: true }).setView([46.6, 2.4], 5); // France
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);
        mapRef.current = map;
        layerRef.current = L.layerGroup().addTo(map);
        return () => { map.remove(); mapRef.current = null; };
    }, []);

    const filtered = useMemo(() => {
        const t = search.trim().toLowerCase();
        const pts = data.points || [];
        if (!t) return pts;
        return pts.filter((p) =>
            String(p.clientName).toLowerCase().includes(t) ||
            String(p.pinCode).toLowerCase().includes(t) ||
            String(p.ip).toLowerCase().includes(t) ||
            String(p.city).toLowerCase().includes(t) ||
            String(p.country).toLowerCase().includes(t));
    }, [data.points, search]);

    // Redessine les marqueurs à chaque changement de filtre.
    useEffect(() => {
        const map = mapRef.current, layer = layerRef.current;
        if (!map || !layer) return;
        layer.clearLayers();
        filtered.forEach((p) => {
            const st = statusOf(p);
            L.marker([p.lat, p.lon], { icon: dot(COLORS[st]) })
                .bindPopup(
                    `<b>${esc(p.clientName || "(sans nom)")}</b><br>` +
                    `PIN ${esc(p.pinCode)} &middot; ${esc(p.appType)}<br>` +
                    `${esc(p.city)}${p.region ? ", " + esc(p.region) : ""} — ${esc(p.country)}<br>` +
                    `<span style="color:#64748b">${esc(p.ip)} &middot; ${esc(p.isp)}</span><br>` +
                    `<span style="color:#64748b">Vu : ${esc(p.lastSeen || "jamais")}</span>`)
                .addTo(layer);
        });
        // Recadre sur le résultat filtré — mais pas sur le chargement initial complet, sinon la
        // vue saute à chaque frappe.
        if (search.trim() && filtered.length > 0) {
            map.fitBounds(L.latLngBounds(filtered.map((p) => [p.lat, p.lon])), { padding: [40, 40], maxZoom: 12 });
        }
    }, [filtered, search]);

    const totals = data.totals || {};
    const counts = useMemo(() => {
        const c = { online: 0, offline: 0, unauthorized: 0 };
        (filtered || []).forEach((p) => { c[statusOf(p)] += 1; });
        return c;
    }, [filtered]);

    return (
        <div className="p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Carte des players</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Positions déduites de l'IP publique — {totals.located || 0} localisés sur {totals.devices || 0} devices
                        {totals.unlocated ? ` (${totals.unlocated} IP non localisables)` : ""}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <span className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS.online }} /> en ligne {counts.online}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS.offline }} /> hors ligne {counts.offline}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS.unauthorized }} /> non autorisé {counts.unauthorized}
                    </span>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filtrer : nom, PIN, IP, ville…"
                        className="h-9 w-64 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                    />
                </div>
            </div>

            {error && (
                <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200">{error}</div>
            )}

            <div className="relative">
                <div ref={mapDiv} className="h-[70vh] w-full rounded-2xl shadow-sm ring-1 ring-slate-200 dark:ring-slate-700" />
                {loading && (
                    <div className="absolute inset-0 grid place-items-center rounded-2xl bg-white/70 text-slate-500">
                        Géolocalisation en cours…
                    </div>
                )}
            </div>

            {/* Honnêteté sur la donnée : ces points ne sont PAS les adresses des clients. */}
            <p className="mt-3 text-xs text-slate-400">
                {data.accuracy || "Position indicative (point de présence de l'opérateur), pas l'adresse exacte."}
                {" "}Filtre affiché : {filtered.length} / {(data.points || []).length}.
            </p>
        </div>
    );
}
